/**
 * SDMS runtime in-cell dropdowns that OG ALPACA adds in code (not authored into the
 * template, so `collectDataValidations` can't see them). Sibling of
 * sdmsQualifierDropdown.ts (the Qualifier column) and applyDeptChainDropdowns.
 *
 * IsReport — OG `SpreadsheetEntryController.AddIsReportCombo` (:9604): a `"True,False"`
 * list validation on the ISReport column. OG anchors it on the DataParsing `Position`
 * for FieldName='ISReport' and fills it down the sample band (worksheet[0] for
 * BatchSampleEntry, each per-sample sheet for SampleSheetNavigation). The G6 equivalent
 * anchors on the merge's `fieldRefs["ISREPORT"]` ("Sheet!A1" — the same anchor the
 * QCBATCHID stamp + the qualifier dropdown use) and fills the band on every result
 * sheet; the list is a 2-value INLINE list, so no helper sheet is needed.
 *
 * (Mold — OG `MoldParametersDropDownSelect` (:29596): a C7:C33 list on Mold_Air/Mold_Bulk
 * sheets sourced from the `SDMSMoldParm` keyed query — deliberately NOT ported here: it
 * is one piece of the larger, un-built Mold SDMS flow and its list query is not
 * configured on the dev DB, so it can't be traced or validated. Track it with that flow.)
 *
 * Runs as a small post-merge step; strict NO-OP when the template has no ISReport field.
 */
import { isResultSheet } from './sdmsEngineOverlay';
import { parseCellRef } from './sdmsDeptChainDropdown';
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';

/** True for a sheet carrying the sample result band (main + per-run/level copies) —
 *  NOT a sub/config sheet. The ISReport column sits at the same position on each. */
function isResultSheetAt(name: string, index: number): boolean {
  return isResultSheet(name, index);
}

/**
 * Inject the ISReport `"True,False"` list dropdown into the merged workbook `buf`.
 * Returns the bytes UNCHANGED when there is no ISReport field (unparseable / absent
 * anchor) — i.e. the ~11 of 51 live templates without an ISReport column.
 *
 * @param isReportRef  fieldRefs["ISREPORT"] — "Sheet!A1" of the ISReport column's first
 *                      data-band cell for this merge.
 */
export async function applyIsReportDropdown(
  buf: ArrayBuffer,
  isReportRef: string | undefined | null,
): Promise<ArrayBuffer> {
  if (!isReportAnchor(isReportRef)) return buf;
  const wb = await loadSdmsWorkbook(buf);
  // ExcelJS drops `text=` from containsText conditional-formatting rules on write, which
  // makes DevExpress (ALPACA) refuse the workbook; writeSdmsWorkbook restores it (no-op when
  // none). Same guard the qualifier / dept-chain injections use. See sdmsCfTextRepair.ts.
  return applyIsReportDropdownToWorkbook(wb, isReportRef) ? writeSdmsWorkbook(wb) : buf;
}

function isReportAnchor(isReportRef: string | undefined | null): { col: number; row: number } | null {
  if (!isReportRef) return null;
  const bang = isReportRef.lastIndexOf('!');
  if (bang < 0) return null;
  return parseCellRef(isReportRef.slice(bang + 1));   // { col, row } 1-based
}

/** The same injection on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns
 *  false, leaving `wb` untouched, when there is no ISReport anchor or no result sheet. */
export function applyIsReportDropdownToWorkbook(wb: import('exceljs').Workbook, isReportRef: string | undefined | null): boolean {
  const anchor = isReportAnchor(isReportRef);
  if (!anchor) return false;
  let applied = false;
  for (const ws of wb.worksheets) {
    if (!isResultSheetAt(ws.name, wb.worksheets.indexOf(ws))) continue;
    // The merge trims empty rows below the band, so the last populated row ≈ the last
    // data row. Fill the dropdown DOWN the whole band (OG fills the sample count).
    let lastRow = 1;
    ws.eachRow({ includeEmpty: false }, (_row, n) => { if (n > lastRow) lastRow = n; });
    const endRow = Math.max(anchor.row, lastRow);
    for (let r = anchor.row; r <= endRow; r++) {
      // Inline 2-value list (OG's "True,False") — no helper sheet needed.
      ws.getCell(r, anchor.col).dataValidation = { type: 'list', allowBlank: true, formulae: ['"True,False"'] };
      applied = true;
    }
  }
  return applied;
}
